import React, { useCallback, useEffect, useRef } from 'react';
import { GridRowId, gridRowSelectionStateSelector } from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';

/**
 * Keeps selected rows selected when a search swaps the grid rows out, which the grid does not do on its
 * own - it deselects any row that leaves the data source. Pair with keepNonExistentRowsSelected to turn
 * that off, and rows that have really gone, deleted ones for example, are deselected here instead.
 *
 * @param {React.MutableRefObject<GridApiPremium>} apiRef - a mutable ref to the grid api
 * @param {any[]} rows - the rows currently given to the grid
 * @param {boolean} enabled - whether the grid allows more than one row to be selected
 * @returns {{ onSearchChange: () => void }} tell the hook a search ran, so the next rows are its results
 */
export function useRowSelectionAcrossSearch(
  apiRef: React.MutableRefObject<GridApiPremium>,
  rows: any[],
  enabled?: boolean
) {
  const searchChangedRef = useRef<boolean>(false);
  const previousRowIdsRef = useRef<Set<GridRowId>>(new Set());

  const onSearchChange = useCallback((): void => {
    searchChangedRef.current = true;
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const rowIds = new Set<GridRowId>(rows.map(row => row.id));
    const previousRowIds = previousRowIdsRef.current;
    const showingSameRows = rowIds.size === previousRowIds.size && rows.every(row => previousRowIds.has(row.id));

    if (showingSameRows) {
      return;
    }

    previousRowIdsRef.current = rowIds;

    const rowsAreSearchResults = searchChangedRef.current;
    searchChangedRef.current = false;

    if (rowsAreSearchResults) {
      return;
    }

    const hasGone = (id: GridRowId) => previousRowIds.has(id) && !rowIds.has(id);

    const selection = gridRowSelectionStateSelector(apiRef.current.state);
    const remainingSelection = selection.filter(id => !hasGone(id));

    if (remainingSelection.length !== selection.length) {
      apiRef.current.setRowSelectionModel(remainingSelection);
    }
  }, [apiRef, enabled, rows]);

  return { onSearchChange };
}
